import apiService from './api';

export interface ReportCategory {
  id: string;
  title: string;
  description: string;
  color: string;
  icon: string;
  reports: Report[];
}

export interface Report {
  id: string;
  name: string;
  description: string;
  category: string;
  roleAccess: string[];
  parameters: ReportParameter[];
  lastGenerated?: string;
  status: 'available' | 'generating' | 'error';
}

export interface ReportParameter {
  name: string;
  type: 'date' | 'dateRange' | 'select' | 'text' | 'number';
  label: string;
  required: boolean;
  options?: string[];
  defaultValue?: any;
}

export interface ReportGenerationRequest {
  reportId: string;
  parameters: any;
  format?: string;
}

export interface ReportGenerationResponse {
  id: string; // The backend returns the UUID of the generated report
  reportId?: string; // Keep for backward compatibility
  status?: string;
  downloadUrl?: string;
  generatedAt?: string;
  expiresAt?: string;
}

export interface ReportStatus {
  reportId: string;
  status: 'pending' | 'generating' | 'completed' | 'error';
  progress?: number;
  downloadUrl?: string;
  error?: string;
  generatedAt?: string;
  expiresAt?: string;
}

export interface ReportHistoryItem {
  reportId: string;
  reportName: string;
  category: string;
  status: string;
  generatedAt: string;
  expiresAt?: string;
  downloadUrl?: string;
  parameters: any;
}

export interface ScheduledReport {
  scheduleId: string;
  reportId: string;
  reportName: string;
  category: string;
  schedule: any;
  status: string;
  nextRun?: string;
  lastRun?: string;
}

export interface ScheduleRequest {
  reportId: string;
  parameters: any;
  schedule: {
    frequency: 'daily' | 'weekly' | 'monthly';
    time: string;
    dayOfWeek?: number;
    dayOfMonth?: number;
    timezone: string;
  };
}

class ReportsApiService {
  // Get all report categories
  async getCategories(): Promise<ReportCategory[]> {
    return apiService.makeRequest('/analytics-reports/categories');
  }

  // Get reports by category
  async getReportsByCategory(categoryId: string): Promise<Report[]> {
    return apiService.makeRequest(`/analytics-reports/category/${categoryId}`);
  }

  // Get all reports
  async getReports(): Promise<any[]> {
    return apiService.makeRequest('/analytics-reports/reports');
  }

  // Generate a report
  async generateReport(request: ReportGenerationRequest): Promise<ReportGenerationResponse> {
    console.log('Reports API - generateReport called with:', request);
    try {
      const response = await apiService.makeRequest('/analytics-reports/generate', {
        method: 'POST',
        body: JSON.stringify(request),
      });
      console.log('Reports API - generateReport response:', response);
      return response;
    } catch (error) {
      console.error('Reports API - generateReport error:', error);
      throw error;
    }
  }

  // Get report status
  async getReportStatus(reportId: string): Promise<ReportStatus> {
    console.log('Getting report status for ID:', reportId);
    try {
      const response = await apiService.makeRequest(`/analytics-reports/status/${reportId}`);
      console.log('Report status response:', response);
      return response;
    } catch (error) {
      console.error('Error getting report status:', error);
      throw error;
    }
  }

  // Get report history
  async getReportHistory(page: number = 1, limit: number = 20): Promise<{
    history: ReportHistoryItem[];
    page: number;
    pageSize: number;
  }> {
    return apiService.makeRequest(`/analytics-reports/history?page=${page}&limit=${limit}`);
  }

  // Delete a report
  async deleteReport(reportId: string): Promise<{ success: boolean }> {
    return apiService.makeRequest(`/analytics-reports/${reportId}`, {
      method: 'DELETE',
    });
  }

  // Schedule a report
  async scheduleReport(request: ScheduleRequest): Promise<{
    scheduleId: string;
    reportId: string;
    status: string;
    nextRun?: string;
  }> {
    return apiService.makeRequest('/analytics-reports/schedule', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  // Get scheduled reports
  async getScheduledReports(): Promise<ScheduledReport[]> {
    return apiService.makeRequest('/analytics-reports/scheduled');
  }

  // Update scheduled report
  async updateScheduledReport(
    scheduleId: string,
    updates: {
      parameters?: any;
      schedule?: any;
      status?: string;
    }
  ): Promise<{
    scheduleId: string;
    status: string;
    nextRun?: string;
  }> {
    return apiService.makeRequest(`/analytics-reports/scheduled/${scheduleId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  // Delete scheduled report
  async deleteScheduledReport(scheduleId: string): Promise<{ success: boolean }> {
    return apiService.makeRequest(`/analytics-reports/scheduled/${scheduleId}`, {
      method: 'DELETE',
    });
  }

  // Download report
  downloadReport(reportId: string): void {
    window.open(`/api/analytics-reports/download/${reportId}`, '_blank');
  }

  // Poll report status until complete
  async pollReportStatus(
    reportId: string,
    onProgress?: (progress: number) => void,
    maxAttempts: number = 30
  ): Promise<ReportStatus> {
    console.log('Starting to poll report status for ID:', reportId);
    let attempts = 0;
    
    while (attempts < maxAttempts) {
      console.log(`Polling attempt ${attempts + 1}/${maxAttempts}`);
      const status = await this.getReportStatus(reportId);
      console.log('Current status:', status);
      
      if (onProgress && status.progress !== undefined) {
        onProgress(status.progress);
      }
      
      if (status.status === 'completed' || status.status === 'error') {
        console.log('Polling completed with status:', status.status);
        return status;
      }
      
      // Wait 2 seconds before next poll
      await new Promise(resolve => setTimeout(resolve, 2000));
      attempts++;
    }
    
    console.log('Polling timed out after', maxAttempts, 'attempts');
    throw new Error('Report generation timed out');
  }

  // Anatomical Reports - Get patient orders for date range
  async getPatientOrdersForAnatomicalReports(
    patientId: string,
    startDate: string,
    endDate: string,
    status: string = 'Completed / Finalized'
  ): Promise<{
    orders: any[];
    total: number;
    patientId: string;
    dateRange: { startDate: string; endDate: string };
    status: string;
  }> {
    return apiService.makeRequest(
      `/analytics-reports/anatomical/patient-orders?patientId=${patientId}&startDate=${startDate}&endDate=${endDate}&status=${status}`
    );
  }

  // Generate Surgical Resection Report
  async generateSurgicalResectionReport(parameters: {
    patientId: string;
    dateRange: { start: string; end: string };
    specimenType?: string;
  }): Promise<ReportGenerationResponse> {
    return apiService.makeRequest('/analytics-reports/anatomical/surgical-resection', {
      method: 'POST',
      body: JSON.stringify({
        patientId: parameters.patientId,
        dateRange: parameters.dateRange,
        specimenType: parameters.specimenType || 'All'
      }),
    });
  }

  // Generate Core Needle Biopsy Report
  async generateCoreNeedleBiopsyReport(parameters: {
    patientId: string;
    dateRange: { start: string; end: string };
    biopsySite?: string;
  }): Promise<ReportGenerationResponse> {
    return apiService.makeRequest('/analytics-reports/anatomical/core-needle-biopsy', {
      method: 'POST',
      body: JSON.stringify({
        patientId: parameters.patientId,
        dateRange: parameters.dateRange,
        biopsySite: parameters.biopsySite || 'All'
      }),
    });
  }

  // Generate FNA Cytology Report
  async generateFNACytologyReport(parameters: {
    patientId: string;
    dateRange: { start: string; end: string };
    specimenType?: string;
  }): Promise<ReportGenerationResponse> {
    return apiService.makeRequest('/analytics-reports/anatomical/fna-cytology', {
      method: 'POST',
      body: JSON.stringify({
        patientId: parameters.patientId,
        dateRange: parameters.dateRange,
        specimenType: parameters.specimenType || 'All'
      }),
    });
  }

  // Generate Frozen Section Report
  async generateFrozenSectionReport(parameters: {
    patientId: string;
    dateRange: { start: string; end: string };
    specimenSite?: string;
  }): Promise<ReportGenerationResponse> {
    return apiService.makeRequest('/analytics-reports/anatomical/frozen-section', {
      method: 'POST',
      body: JSON.stringify({
        patientId: parameters.patientId,
        dateRange: parameters.dateRange,
        specimenSite: parameters.specimenSite || 'All'
      }),
    });
  }

  // Generate IHC Panel Report
  async generateIHCPanelReport(parameters: {
    patientId: string;
    dateRange: { start: string; end: string };
    ihcPanel?: string;
  }): Promise<ReportGenerationResponse> {
    return apiService.makeRequest('/analytics-reports/anatomical/ihc-panel', {
      method: 'POST',
      body: JSON.stringify({
        patientId: parameters.patientId,
        dateRange: parameters.dateRange,
        ihcPanel: parameters.ihcPanel || 'All'
      }),
    });
  }
}

export const reportsApiService = new ReportsApiService();
export default reportsApiService; 